import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';
import { Footer } from '../components/Footer';
import { Navigation } from '../components/Navigation';
import './PricingPage.css';

/** Total founding-cohort seats (kept in sync with the worker's TOTAL_SEATS). */
const SEATS_TOTAL = 35;

/** Early-access form endpoint; the live seat count lives at `${API_URL}/count`. */
const API_URL = import.meta.env.VITE_EARLY_ACCESS_API_URL ?? '';
const COUNT_URL = API_URL ? `${API_URL.replace(/\/$/, '')}/count` : '';

type Billing = 'monthly' | 'yearly';

interface Tier {
  name: string;
  /** Punchy hunt/farm framing — the accent line directly under the name. */
  tagline: string;
  /** Plain module composition, e.g. "Subscriptions + CRM". */
  modules: string;
  monthly: number;
  yearly: number;
  /** Metered charge on top of the plan price, e.g. Support's per-resolution fee. */
  usage?: string;
  available: boolean;
  highlight: boolean;
  features: string[];
}

const TIERS: Tier[] = [
  {
    name: 'Subscription Management',
    tagline: 'Run the revenue engine',
    modules: 'Subscriptions',
    monthly: 59,
    yearly: 599,
    available: true,
    highlight: false,
    features: [
      'Subscription & billing engine',
      'Plans, pricing & invoicing',
      'Unified revenue dashboard (MRR / ARR / churn)',
      '5 team members included',
    ],
  },
  {
    name: 'CRM',
    tagline: 'Hunt new revenue',
    modules: 'Subscriptions + CRM',
    monthly: 99,
    yearly: 999,
    available: false,
    highlight: false,
    features: [
      'Everything in Subscription Management',
      'Built-in CRM',
      'Customer & deal tracking',
      'Forecasting & cash flow planning',
      'Gmail integration',
      '5 team members included',
    ],
  },
  {
    name: 'Support',
    tagline: 'Keep every customer',
    modules: 'Subscriptions + Support',
    monthly: 99,
    yearly: 999,
    usage: '+ $0.35 per AI resolution',
    available: false,
    highlight: false,
    features: [
      'Everything in Subscription Management',
      'Shared support inbox & ticketing',
      'In-app support chat for your customers',
      'AI agent that resolves tickets',
      'Billing context on every ticket',
      '5 team members included',
    ],
  },
  {
    name: 'Full Platform',
    tagline: 'Hunt, farm and support, connected',
    modules: 'Subscriptions + CRM + Support',
    monthly: 149,
    yearly: 1499,
    usage: '+ $0.35 per AI resolution',
    available: false,
    highlight: true,
    features: [
      'Everything in Subscription Management',
      'Everything in CRM',
      'Everything in Support',
      'AI-powered insights',
      'Forecasting & budgeting integrated',
      '5 team members included',
    ],
  },
];

interface AddOn {
  name: string;
  description: string;
  monthly: number;
  yearly: number;
  /** What one unit of the add-on is, e.g. "per member". */
  unit?: string;
}

const ADD_ONS: AddOn[] = [
  {
    name: 'OKRs',
    description:
      'Company and team objectives with Key Results tracked against your revenue data. Works with any plan.',
    monthly: 29,
    yearly: 299,
  },
  {
    name: 'Additional team member',
    description: 'Every plan includes 5 team members. Add as many more as you need.',
    monthly: 9,
    yearly: 90,
    unit: 'per member',
  },
];

function CheckIcon() {
  return (
    <svg
      className="pricing__check"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function PricingPage() {
  useSeo({
    title: 'Pricing — Revvly Subscription Revenue Management',
    description:
      'Revvly plans and modules for SaaS billing, pricing, CRM and revenue analytics, with founding-cohort pricing for early customers.',
    path: '/pricing',
    type: 'website',
  });

  const [billing, setBilling] = useState<Billing>('monthly');
  const yearly = billing === 'yearly';

  // Live founding-seat data from the early-access worker; null until loaded.
  const [seatsRemaining, setSeatsRemaining] = useState<number | null>(null);
  const [seatsTotal, setSeatsTotal] = useState<number>(SEATS_TOTAL);
  useEffect(() => {
    if (!COUNT_URL) return undefined;
    const controller = new AbortController();
    fetch(COUNT_URL, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { ok?: boolean; remaining?: number; total?: number } | null) => {
        if (data?.ok && typeof data.remaining === 'number') {
          // Trust the worker's own total (falls back to the local default).
          const total = typeof data.total === 'number' ? data.total : SEATS_TOTAL;
          setSeatsTotal(total);
          setSeatsRemaining(Math.max(0, Math.min(total, data.remaining)));
        }
      })
      .catch(() => {
        /* Non-blocking: fall back to the static copy if the count is unavailable. */
      });
    return () => controller.abort();
  }, []);

  const soldOut = seatsRemaining === 0;

  return (
    <div className="pricing">
      <Navigation />

      <main className="pricing__main">
        <div className="pricing__inner">
          <header className="pricing__header">
            <p className="pricing__eyebrow">
              <span className="pricing__eyebrow-rule" aria-hidden />
              Pricing
            </p>
            <h1 className="pricing__title">
              Built to grow with your <em className="pricing__accent">company</em>.
            </h1>
            <p className="pricing__lead">
              Start with subscriptions today. Add CRM and support as they ship. Every plan includes
              5 team members. Add more, or OKRs, as add-ons.
            </p>
          </header>

          <section className="pricing__offer" aria-label="Founding offer">
            <div className="pricing__offer-copy">
              <span className="pricing__pulse" aria-hidden />
              <div>
                <p className="pricing__offer-title">First {seatsTotal} sign-ups: 6 months free</p>
                <p className="pricing__offer-sub">
                  One 15-min monthly call with our CEO · every new module free as it ships
                </p>
              </div>
            </div>
            <div className="pricing__offer-action">
              <span className="pricing__offer-seats">
                {soldOut
                  ? 'Founding cohort full'
                  : `${seatsRemaining ?? seatsTotal} of ${seatsTotal} seats left`}
              </span>
              <Link to="/early-access" className="pricing__btn pricing__btn--accent">
                {soldOut ? 'Join the waitlist' : 'Claim a seat'} &rsaquo;
              </Link>
            </div>
          </section>

          <div className="pricing__toggle-wrap">
            <div className="pricing__toggle" role="group" aria-label="Billing period">
              <button
                type="button"
                className={`pricing__toggle-btn${!yearly ? ' pricing__toggle-btn--active' : ''}`}
                aria-pressed={!yearly}
                onClick={() => setBilling('monthly')}
              >
                Monthly
              </button>
              <button
                type="button"
                className={`pricing__toggle-btn${yearly ? ' pricing__toggle-btn--active' : ''}`}
                aria-pressed={yearly}
                onClick={() => setBilling('yearly')}
              >
                Yearly <span className="pricing__toggle-hint">· save ~2 months</span>
              </button>
            </div>
          </div>

          <section className="pricing__grid">
            {TIERS.map((tier) => {
              const savings = tier.monthly * 12 - tier.yearly;
              return (
                <article
                  key={tier.name}
                  className={`pricing__card${tier.highlight ? ' pricing__card--highlight' : ''}`}
                >
                  <div className="pricing__card-top">
                    <span
                      className={`pricing__status${
                        tier.available ? ' pricing__status--available' : ''
                      }`}
                    >
                      {tier.available ? 'Available now' : 'Coming soon'}
                    </span>
                    {tier.highlight && <span className="pricing__badge">Best value</span>}
                  </div>

                  <h2 className="pricing__card-name">{tier.name}</h2>
                  <p className="pricing__card-tagline">{tier.tagline}</p>
                  <p className="pricing__card-modules">{tier.modules}</p>

                  <div className="pricing__price">
                    <span className="pricing__price-amount">
                      ${yearly ? tier.yearly : tier.monthly}
                    </span>
                    <span className="pricing__price-period">{yearly ? '/year' : '/month'}</span>
                  </div>
                  <p className="pricing__price-note">
                    {yearly ? `billed annually · save $${savings}` : 'billed monthly'}
                  </p>
                  {/* Rendered on every card so the CTAs stay aligned across the grid. */}
                  <p className="pricing__price-usage">{tier.usage}</p>

                  <Link
                    to={tier.available ? '/early-access' : undefined}
                    className={`pricing__btn pricing__card-cta${
                      tier.available ? ' pricing__btn--accent' : ' pricing__btn--ghost'
                    }`}
                  >
                    {tier.available ? 'Start free trial' : 'Coming soon'}
                  </Link>

                  <div className="pricing__divider" aria-hidden />

                  <ul className="pricing__features">
                    {tier.features.map((feature) => (
                      <li key={feature} className="pricing__feature">
                        <CheckIcon />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </section>

          <section className="pricing__addons" aria-labelledby="pricing-addons-title">
            <h2 id="pricing-addons-title" className="pricing__addons-title">
              Add-ons
            </h2>
            <div className="pricing__addons-grid">
              {ADD_ONS.map((addOn) => {
                const savings = addOn.monthly * 12 - addOn.yearly;
                return (
                  <article key={addOn.name} className="pricing__card pricing__addon">
                    <h3 className="pricing__card-name">{addOn.name}</h3>
                    <p className="pricing__card-modules">{addOn.description}</p>
                    <div className="pricing__price">
                      <span className="pricing__price-amount">
                        ${yearly ? addOn.yearly : addOn.monthly}
                      </span>
                      <span className="pricing__price-period">
                        {yearly ? '/year' : '/month'}
                        {addOn.unit ? ` ${addOn.unit}` : ''}
                      </span>
                    </div>
                    <p className="pricing__price-note">
                      {yearly ? `billed annually · save $${savings}` : 'billed monthly'}
                    </p>
                  </article>
                );
              })}
            </div>
          </section>

          <p className="pricing__footnote">
            Need a custom setup?{' '}
            <a href="mailto:alain@revvly.ai" className="pricing__footnote-link">
              Talk to the founders &rsaquo;
            </a>
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
